import type { UseQueryResult } from '@tanstack/react-query'
import { ApiError } from '../../core/index.js'
import type { BarrierActions } from '../../messages/index.js'
import type { BehaviorEvent } from '../../behaviors/index.js'
import type { FacilityConfig } from '../../facilities/index.js'
import type { ScoreBreakdown, ScoreHistory, WorkerScore } from '../../scoring/index.js'
import type { ShiftOutcome } from '../../partner/index.js'
import type { WorkerBarrier, WorkerNotification } from '../../messages/index.js'
import type { WorkerBidWindow } from '../../schedule/index.js'
import type { WorkerProfile } from '../index.js'
import { useAcknowledgeBarrier, useReplyToBarrier, useWorkerBarriers, useWorkerNotifications } from '../../messages/index.js'

import { useFacilityConfig } from '../../facilities/index.js'

import { useScoreBreakdown, useWorkerScore, useWorkerScoreForPeriod, useWorkerScoreHistory } from '../../scoring/index.js'

import { useWorkerBehaviorEvents } from '../../behaviors/index.js'

import { useWorkerBidWindow } from '../../schedule/index.js'

import { useWorkerOutcomes } from '../../partner/index.js'

import { useWorkerProfile } from '../index.js'

/**
 * Every read the worker scoring panel makes, as hooks, so the same panel
 * can sit in the operator app (bearer-token reads of `/workers/...`) and
 * the admin area (Basic Auth reads of `/admin/clients/.../workers/...`).
 * An implementation must call the same hooks in the same order on every
 * render, as any hook must; each takes null to stay idle.
 */
export interface WorkerDetailHooks {
  useProfile(workerId: string | null): UseQueryResult<WorkerProfile>
  useScoreBreakdown(workerId: string | null): UseQueryResult<ScoreBreakdown>
  /** The current running score. */
  useScore(workerId: string | null): UseQueryResult<WorkerScore>
  /** A past period's score (`YYYY-MM`), or null when the period has none. */
  useScoreForPeriod(workerId: string | null, period: string | null): UseQueryResult<WorkerScore | null>
  useScoreHistory(workerId: string | null): UseQueryResult<ScoreHistory>
  useBidWindow(workerId: string | null): UseQueryResult<WorkerBidWindow>
  useNotifications(workerId: string | null): UseQueryResult<WorkerNotification[]>
  useOutcomes(workerId: string | null): UseQueryResult<ShiftOutcome[]>
  useBehaviorEvents(workerId: string | null): UseQueryResult<BehaviorEvent[]>
  /** What the worker has said is getting in the way, newest first. */
  useBarriers(workerId: string | null): UseQueryResult<WorkerBarrier[]>
  /** Marks a note seen, replies to it. Absent where the reader may not (the platform admin reads only). */
  barrierActions?: BarrierActions
  /** The facility's config, for the day rules the month calendars shade by. */
  useFacilityConfig(facilityId: string | null): UseQueryResult<FacilityConfig>
}

/** The operator app's reads, keyed as they always were so the rest of the app's invalidations still reach them. */
export const operatorWorkerDetailHooks: WorkerDetailHooks = {
  useProfile: useWorkerProfile,
  useScoreBreakdown,
  useScore: useWorkerScore,
  useScoreForPeriod: useWorkerScoreForPeriod,
  useScoreHistory: useWorkerScoreHistory,
  useBidWindow: useWorkerBidWindow,
  useNotifications: useWorkerNotifications,
  useOutcomes: useWorkerOutcomes,
  useBehaviorEvents: useWorkerBehaviorEvents,
  useBarriers: useWorkerBarriers,
  useFacilityConfig,
}

/** The staff actions on a note, bound to the operator app's mutations; built inside a component since each mutation is a hook. */
export function useBarrierActions(): BarrierActions {
  const acknowledge = useAcknowledgeBarrier()
  const reply = useReplyToBarrier()
  return {
    onAcknowledge: (barrier) => acknowledge.mutate({ workerId: barrier.workerId, barrierId: barrier.barrierId }),
    acknowledging: acknowledge.isPending ? (acknowledge.variables?.barrierId ?? null) : null,
    acknowledgeError: acknowledge.isError ? 'Could not mark that note seen. Try again.' : null,
    onReply: (barrier, message) => reply.mutate({ workerId: barrier.workerId, barrierId: barrier.barrierId, message }),
    replying: reply.isPending ? (reply.variables?.barrierId ?? null) : null,
    replyError: reply.isError && reply.variables ? { barrierId: reply.variables.barrierId, message: reply.error instanceof ApiError ? reply.error.message : 'Your reply could not be sent. Try again in a moment.' } : null,
  }
}

/** The operator's hooks with the staff actions bound. */
export function useOperatorWorkerDetailHooks(): WorkerDetailHooks {
  return { ...operatorWorkerDetailHooks, barrierActions: useBarrierActions() }
}
